import {
  notificationChannels,
  notificationEventInfo,
  type EffectivePreferences,
  type NotificationChannel,
  type NotificationEvent,
} from '@smarton-pbx/shared';
import { Bell } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api';
import { Loading, Notice } from '../components/ui';
import { t } from '../i18n';
import { useLoad } from '../lib/hooks';

export const eventNames: Record<NotificationEvent, { label: string; hint: string }> = {
  'incoming-call': { label: t('Incoming call'), hint: t('When the app is closed, with Answer and Decline') },
  'missed-call': { label: t('Missed call'), hint: t('A call to you that nobody answered') },
  voicemail: { label: t('New voicemail'), hint: t('A caller left you (or your queue) a message') },
  text: { label: t('New text'), hint: t('A text (SMS) to your number, or one whose texts come to you') },
  'queue-callback': { label: t('Queue caller to call back'), hint: t('A call to your queue went unanswered, or the caller asked to be called back') },
  'queue-waiting': { label: t('Caller waiting long'), hint: t('Someone has waited long in a queue you manage') },
  'weekly-digest': { label: t('Weekly coaching digest'), hint: t('Mondays: what went well last week and what to improve') },
  'call-blocked': { label: t('Call blocked'), hint: t('Someone tried an emergency, premium-rate or not-allowed number') },
  'trunk-down': { label: t('Carrier not connected'), hint: t('Calls through it may fail') },
  'ai-budget-reached': { label: t('AI budget used up'), hint: t('Call reviews pause until next month') },
};

const channelNames: Record<NotificationChannel, string> = { inApp: t('In the app'), push: t('On the computer'), phone: t('On the phone') };

/** Events × channels, as checkboxes. */
export function PreferencesMatrix({
  events,
  value,
  onChange,
  testId,
}: {
  events: readonly NotificationEvent[];
  value: EffectivePreferences;
  onChange: (value: EffectivePreferences) => void;
  testId?: string;
}) {
  const toggle = (event: NotificationEvent, channel: NotificationChannel, on: boolean) =>
    onChange({ ...value, [event]: { ...value[event], [channel]: on } });
  return (
    <table className="preferences" data-testid={testId}>
      <thead>
        <tr>
          <th />
          {notificationChannels.map((channel) => (
            <th key={channel} className="num">
              {channelNames[channel]}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {events.map((event) => (
          <tr key={event}>
            <td>
              <div style={{ fontWeight: 560 }}>{eventNames[event].label}</div>
              <div className="subtle" style={{ fontSize: '0.83rem' }}>
                {eventNames[event].hint}
              </div>
            </td>
            {notificationChannels.map((channel) => (
              <td key={channel} className="num">
                {notificationEventInfo[event].channels.includes(channel) ? (
                  <input
                    type="checkbox"
                    checked={value[event][channel]}
                    onChange={(e) => toggle(event, channel, e.target.checked)}
                    aria-label={`${eventNames[event].label}: ${channelNames[channel]}`}
                    data-testid={`pref-${event}-${channel}`}
                  />
                ) : (
                  <span className="subtle" aria-hidden>
                    —
                  </span>
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The person's own notification choices (Account page). */
export function MyNotifications() {
  const loaded = useLoad(api.notificationPreferences);
  const [value, setValue] = useState<EffectivePreferences | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string>();
  const current = value ?? loaded.data?.preferences ?? null;

  async function save() {
    if (!current || !loaded.data) return;
    setSaved(false);
    setError(undefined);
    try {
      // Only the events this person gets.
      const own = Object.fromEntries(loaded.data.events.map((event) => [event, current[event]]));
      await api.updateNotificationPreferences(own);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="row" style={{ gap: '0.6rem' }}>
          <Bell size={18} className="subtle" aria-hidden />
          <h2>{t('Notifications')}</h2>
        </div>
      </div>
      <div className="card-body stack">
        {!loaded.data || !current ? (
          <Loading />
        ) : (
          <>
            <PreferencesMatrix
              events={loaded.data.events}
              value={current}
              onChange={(next) => {
                setSaved(false);
                setValue(next);
              }}
              testId="my-notification-preferences"
            />
            {!loaded.data.pushAvailable && <p className="subtle">{t('Notifications on this computer are not set up on this server.')}</p>}
            <p className="subtle" style={{ fontSize: '0.85rem' }}>
              {t(
                '“On this computer” works while the browser is running, even with the app closed, once you have allowed notifications in this browser. Nothing arrives when the browser is shut, the computer is asleep or do-not-disturb is on.',
              )}
            </p>
            {error && <Notice kind="error">{error}</Notice>}
            {saved && <Notice kind="success">{t('Saved.')}</Notice>}
            <div>
              <button type="button" onClick={() => void save()} data-testid="save-my-notifications">
                {t('Save')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
